import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of, switchMap, tap } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementId,
  CanvasElementProperties,
  CanvasElementType,
} from '@blaze-runtime/engine-domain';
import { utcToCalendarWallTime } from '@blaze-runtime/engine-util';
import {
  DataBindingSourceInfo,
  FormContainerModelField,
  FormContext,
  getActiveBinding,
  RenderElementDataRowContext,
  RuntimeContext,
} from '@blaze-shared/elements-domain';
import { loggerCreate } from '@blaze-shared/logger-util';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { BlazeSchema, type ElementSchema, WorkflowSchema } from '@blaze-shared/schema-domain';
import { deepClone, isNullish } from '@blaze-shared/shared-util';

import { ElementHierarchyService } from '../../../services/element-hierarchy.service';
import { RuntimeService } from '../../../services/runtime.service';
import { getUiRuntimeElement } from '../../../utils/runtime';
import { hasDataProps } from '../../element-model/element-props-guards';
import {
  isElementType,
  isFormList,
  isVariantOfFormContainer,
} from '../../element-model/element-type-guards';
import { DataProviderQuery, InputDataPackage } from '../io-data.models';

import { ApiCallDataProvider } from './providers/api-call.data-provider';
import { ColumnFilterDataProvider } from './providers/column-filter.data-provider';
import { ColumnTagsDataProvider } from './providers/column-tags.data-provider';
import { ContextDataProvider } from './providers/context.data-provider';
import { DataFilterDataProvider } from './providers/data-filter.data-provider';
import { DataSourceProvider } from './providers/data-source.data-provider';
import { DatasetDataProvider } from './providers/dataset-data-provider/dataset.data-provider';
import { ExpressionDataProvider } from './providers/expression.data-provider';
import { FormContainerDataProvider } from './providers/form-container.data-provider';
import { FormGroupDataProvider } from './providers/form-group.data-provider';
import { FormListDataProvider } from './providers/form-list.data-provider';
import { MirrorDataProvider } from './providers/mirror.data-provider';
import { RangedDateDataProvider } from './providers/ranged-date.data-provider';
import { SelfDataProvider } from './providers/self.data-provider';

const log = loggerCreate('data_providers', 'info', 'InputProvider', null);

@Injectable({ providedIn: 'root' })
export class ElementInputProviderService {
  private readonly apiCallDataProvider = inject(ApiCallDataProvider);
  private readonly columnFilterDataProvider = inject(ColumnFilterDataProvider);
  private readonly columnTagsDataProvider = inject(ColumnTagsDataProvider);
  private readonly contextDataProvider = inject(ContextDataProvider);
  private readonly dataFilterDataProvider = inject(DataFilterDataProvider);
  private readonly datasetDataProvider = inject(DatasetDataProvider);
  private readonly elementHierarchyService = inject(ElementHierarchyService);
  private readonly expressionDataProvider = inject(ExpressionDataProvider);
  private readonly formContainerDataProvider = inject(FormContainerDataProvider);
  private readonly formGroupDataProvider = inject(FormGroupDataProvider);
  private readonly formListDataProvider = inject(FormListDataProvider);
  private readonly mirrorDataProvider = inject(MirrorDataProvider);
  private readonly rangedDateDataProvider = inject(RangedDateDataProvider);
  private readonly runtimeService = inject(RuntimeService);
  private readonly schemaService = inject(SchemaService);
  private readonly selfDataProvider = inject(SelfDataProvider);
  private readonly store = inject(Store);

  private readonly providerRegistry: Map<string, DataSourceProvider> = new Map<
    string,
    DataSourceProvider
  >([
    [this.apiCallDataProvider.sourceType, this.apiCallDataProvider],
    [this.columnFilterDataProvider.sourceType, this.columnFilterDataProvider],
    [this.columnTagsDataProvider.sourceType, this.columnTagsDataProvider],
    [this.contextDataProvider.sourceType, this.contextDataProvider],
    [this.dataFilterDataProvider.sourceType, this.dataFilterDataProvider],
    [this.datasetDataProvider.sourceType, this.datasetDataProvider],
    [this.expressionDataProvider.sourceType, this.expressionDataProvider],
    [this.formContainerDataProvider.sourceType, this.formContainerDataProvider],
    [this.formGroupDataProvider.sourceType, this.formGroupDataProvider],
    [this.formListDataProvider.sourceType, this.formListDataProvider],
    [this.mirrorDataProvider.sourceType, this.mirrorDataProvider],
    [this.rangedDateDataProvider.sourceType, this.rangedDateDataProvider],
    [this.selfDataProvider.sourceType, this.selfDataProvider],
  ]);

  getInputDataStream(query: DataProviderQuery): Observable<InputDataPackage> {
    const { source, element, inputName } = query;
    const provider = this.providerRegistry.get(source.sourceType);
    if (!provider) {
      throw new Error(`No data provider registered for source type: "${source.sourceType}"`);
    }
    return provider
      .getDataStream(query)
      .pipe(
        tap((result) =>
          log('getDataStream', (tag) =>
            console.log(
              `${tag} 💨 ${element?.type} | ${element?.id} | ${inputName} | ${source.id}`,
              deepClone(result),
            ),
          ),
        ),
      );
  }

  getColumnFilterDataStream(source: DataBindingSourceInfo): Observable<InputDataPackage> {
    return this.columnFilterDataProvider.getDataStream({
      source,
    });
  }

  getDataAdapter(
    element: CanvasElement,
    isDefaultDataSourceInput: boolean | undefined,
  ): (data: any, formContext?: FormContext) => any {
    if (!isDefaultDataSourceInput) {
      return (data) => data;
    }

    const isTextField = isElementType(element, 'textField');
    // Calendar handling
    if (
      element.type === 'datetimeInput' ||
      (isTextField && element.props.content?.input?.format === 'datetime')
    ) {
      return (data, formContext) => this.handleCalendarTimezone(element, data, formContext);
    }

    if (isVariantOfFormContainer(element)) {
      return (data) => {
        if (!Array.isArray(data)) {
          return data;
        }
        return data.length ? data[0] : this.createEmptyFormContainerRow(element.props);
      };
    } else if (isFormList(element)) {
      return (data) => {
        if (Array.isArray(data)) {
          return data;
        }
        if (isNullish(data)) {
          return [];
        }
        return [data];
      };
    }

    return (data) => data;
  }

  /**
   * Delegates to {@link ExpressionDataProvider}.
   *
   * Kept on this service so external callers do not need to change their injection target.
   */
  getExpressionRuntimeContext(
    elementId: CanvasElementId | undefined,
    elementType: CanvasElementType | undefined,
    rowContext?: RenderElementDataRowContext,
  ): Observable<RuntimeContext> {
    return this.expressionDataProvider.getExpressionRuntimeContext(
      elementId,
      elementType,
      rowContext,
    );
  }

  getParentInputSchema(
    elementId: CanvasElementId | undefined,
    elementType: CanvasElementType,
  ): Observable<ElementSchema | BlazeSchema | WorkflowSchema | undefined> {
    if (!elementId) {
      return of(undefined);
    }
    return this.elementHierarchyService
      .getParentElement(elementId, elementType)
      .pipe(switchMap((element) => this.getInputSchemaFromElement(element)));
  }

  getInputSchemaFromElement(
    element: CanvasElement | undefined,
  ): Observable<ElementSchema | BlazeSchema | WorkflowSchema | undefined> {
    if (!element) {
      return of(undefined);
    }
    return this.store.select(runtimeSelectors.selectSchemaById(element.id)).pipe(
      switchMap((schema) => {
        if (!schema && hasDataProps(element)) {
          const dataBindingSource = getActiveBinding(element.props.data, 'value')?.dataSource;

          if (!dataBindingSource) {
            return of(undefined);
          }
          return this.schemaService.obtainSchema(
            dataBindingSource.id,
            dataBindingSource.sourceType === 'context',
          );
        }
        return of(schema);
      }),
    );
  }

  cleanupBaseProviders(containerId: CanvasElementId): void {
    this.formListDataProvider.cleanupBaseProviders(containerId);
  }

  private handleCalendarTimezone(
    element: CanvasElement,
    data: any,
    formContext?: FormContext,
  ): any {
    if (!data || !hasDataProps(element)) {
      return data;
    }

    const dataSource = element.props.data.find((d) => d.key === 'value')?.dataSource;
    if (!dataSource || dataSource.sourceType !== 'formContainer') {
      return data;
    }

    const columnIdOrName = dataSource.name;
    if (!columnIdOrName) {
      return data;
    }

    const runtimeElement = getUiRuntimeElement(dataSource.id);
    const formData = this.runtimeService.getDataForElement(runtimeElement, 'change');

    // Find TimeZone column
    const timeZoneColumn = formContext?.timeZoneColumn;
    if (!timeZoneColumn) {
      return data;
    }

    const timeZone: string | undefined = formData?.[timeZoneColumn];

    if (!timeZone) {
      return data;
    }

    return utcToCalendarWallTime(new Date(data), timeZone).toISOString();
  }

  private createEmptyFormContainerRow(props: CanvasElementProperties): any {
    const fields = (props as any).content?.model as FormContainerModelField[];
    return fields?.reduce((acc, curr) => {
      acc[curr.field] = null;
      return acc;
    }, {} as any);
  }
}
